/** Test panel: evaluate the current (unsaved) language policy against a context
 *  and show the resolved language + a per-rule trace. The user picks an event,
 *  then supplies a context one of three ways — pasted JSON, an Event_Delivery_Log
 *  id (its archived rendering context), or an entity id (GTU / order / user, per
 *  the event family). Evaluation happens on the backend and never saves the
 *  policy. Mirrors BlockTestPanel. */
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Fragment, useEffect, useState } from 'react';

import { ConsoleApiError } from '@/api/client';
import {
  evaluatePolicy,
  listPolicyEvents,
  type EvaluateContext,
  type PolicyEvaluateResult,
  type PolicyUnderTest,
  type SupportedEvent,
} from '@/api/languagePolicies';

type SourceMode = 'json' | 'event_delivery_log' | 'entity';

const ENTITY_LABEL: Record<'gtu' | 'order' | 'user', string> = {
  gtu: 'Tour participant (GTU) ID',
  order: 'Order ID',
  user: 'User ID',
};

/** Compact single-line JSON for a trace cell. */
function compact(v: unknown): string {
  if (v === undefined) return '—';
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}

interface Props {
  /** Reads the dialog's current editor state into an (unsaved) policy payload. */
  buildPolicy: () => PolicyUnderTest;
}

export function LanguagePolicyTestPanel({ buildPolicy }: Props) {
  const { data: events = [] } = useQuery({
    queryKey: ['language-policy-events'],
    queryFn: listPolicyEvents,
    staleTime: 5 * 60_000,
  });

  const [event, setEvent] = useState<SupportedEvent | null>(null);
  const [mode, setMode] = useState<SourceMode>('json');
  const [jsonText, setJsonText] = useState('');
  const [edlId, setEdlId] = useState('');
  const [entityId, setEntityId] = useState('');

  const entityKind = event?.entity_kind ?? null;
  const entitySupported = entityKind !== null;

  // Keep mode valid: entity is only available for events that support it.
  useEffect(() => {
    if (mode === 'entity' && !entitySupported) setMode('json');
  }, [mode, entitySupported]);

  const mutation = useMutation<PolicyEvaluateResult, unknown, void>({
    mutationFn: () => {
      let context: EvaluateContext;
      if (mode === 'event_delivery_log') context = { mode, event_delivery_log_id: edlId.trim() };
      else if (mode === 'entity') context = { mode, entity_id: Number(entityId) };
      else context = { mode: 'json', json: jsonText };
      return evaluatePolicy({ event: event?.code ?? '', policy: buildPolicy(), context });
    },
  });

  const result = mutation.data;

  const requestError =
    mutation.error instanceof ConsoleApiError
      ? mutation.error.message
      : mutation.error
        ? 'Evaluation request failed.'
        : null;

  const canEvaluate =
    mode === 'json' ||
    (mode === 'event_delivery_log' && edlId.trim() !== '') ||
    (mode === 'entity' && entitySupported && entityId.trim() !== '');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Autocomplete<SupportedEvent>
        size="small"
        options={events}
        value={event}
        onChange={(_, v) => setEvent(v)}
        getOptionLabel={(o) => o.code}
        isOptionEqualToValue={(a, b) => a.code === b.code}
        groupBy={(o) => o.family}
        renderInput={(params) => (
          <TextField {...params} label="Event" placeholder="Choose an event" />
        )}
      />

      <ToggleButtonGroup
        exclusive
        size="small"
        value={mode}
        onChange={(_, v: SourceMode | null) => v && setMode(v)}
      >
        <ToggleButton value="json">Paste JSON</ToggleButton>
        <ToggleButton value="event_delivery_log">Delivery log</ToggleButton>
        <ToggleButton value="entity" disabled={!entitySupported}>
          {entityKind ? ENTITY_LABEL[entityKind].replace(/ ID$/, '') : 'Entity'}
        </ToggleButton>
      </ToggleButtonGroup>

      {mode === 'json' && (
        <TextField
          label="Context JSON"
          size="small"
          fullWidth
          multiline
          minRows={5}
          value={jsonText}
          onChange={(e) => setJsonText(e.target.value)}
          placeholder={'{"order": {"ref": "ABC"}}'}
          sx={{ '& textarea': { fontFamily: 'monospace', fontSize: 12 } }}
        />
      )}
      {mode === 'event_delivery_log' && (
        <TextField
          label="Event Delivery Log ID"
          size="small"
          fullWidth
          value={edlId}
          onChange={(e) => setEdlId(e.target.value)}
          placeholder="UUID — its archived rendering context is fetched"
        />
      )}
      {mode === 'entity' && entitySupported && (
        <TextField
          label={entityKind ? ENTITY_LABEL[entityKind] : 'Entity ID'}
          size="small"
          type="number"
          value={entityId}
          onChange={(e) => setEntityId(e.target.value)}
          helperText={`Builds the ${event?.family} context from this record.`}
        />
      )}

      <Box>
        <Button
          variant="outlined"
          size="small"
          disabled={!canEvaluate || mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? 'Evaluating…' : 'Evaluate'}
        </Button>
      </Box>

      {requestError && <Alert severity="error">{requestError}</Alert>}

      {result && !result.ok && (
        <Alert severity="warning">
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            Evaluation error
          </Typography>
          <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', fontSize: 12 }}>
            {result.error}
          </Box>
        </Alert>
      )}

      {result?.ok && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            Resolved language
          </Typography>
          {result.result != null ? (
            <Box
              sx={{
                mt: 0.5,
                px: 1.5,
                py: 1,
                borderRadius: 1,
                bgcolor: 'action.hover',
                fontFamily: 'monospace',
                fontSize: 18,
                fontWeight: 700,
              }}
            >
              {result.result}
            </Box>
          ) : (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5, fontStyle: 'italic' }}
            >
              No language resolved — falls back to default.
            </Typography>
          )}
        </Box>
      )}

      {result && result.trace.length > 0 && (
        <Box>
          <Typography variant="caption" color="text.secondary">
            Rule trace
          </Typography>
          <Box
            sx={{
              mt: 0.5,
              overflowX: 'auto',
              border: 1,
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            <Table size="small" sx={{ '& td, & th': { px: 1, py: 0.5 } }}>
              <TableHead>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Raw</TableCell>
                  <TableCell>Code</TableCell>
                  <TableCell padding="checkbox" />
                </TableRow>
              </TableHead>
              <TableBody>
                {result.trace.map((t, idx) => (
                  <Fragment key={idx}>
                    <TableRow
                      sx={{
                        bgcolor: t.matched ? 'action.selected' : undefined,
                        ...(t.error ? { '& td': { borderBottom: 'none' } } : {}),
                      }}
                    >
                      <TableCell>{t.order}</TableCell>
                      <TableCell>{t.type ?? '—'}</TableCell>
                      <TableCell
                        sx={{
                          fontFamily: 'monospace',
                          fontSize: 11,
                          maxWidth: 220,
                          wordBreak: 'break-all',
                        }}
                      >
                        {compact(t.raw)}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontWeight: t.matched ? 700 : 400 }}>
                        {t.code ?? '—'}
                      </TableCell>
                      <TableCell padding="checkbox">
                        {t.matched && (
                          <CheckCircleIcon color="success" fontSize="small" aria-label="Winner" />
                        )}
                      </TableCell>
                    </TableRow>
                    {t.error && (
                      <TableRow sx={{ bgcolor: t.matched ? 'action.selected' : undefined }}>
                        <TableCell colSpan={5} sx={{ pt: 0 }}>
                          <Typography variant="caption" color="error">
                            {t.error}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </Fragment>
                ))}
              </TableBody>
            </Table>
          </Box>
        </Box>
      )}

      {result && result.context_keys.length > 0 && (
        <Typography variant="caption" color="text.secondary">
          Context namespaces: {result.context_keys.join(', ')}
        </Typography>
      )}
    </Box>
  );
}
